import {useSearchParams} from "react-router-dom";
import {BucketMapping, DfsClient} from "../../client/DfsClient";
import {useEffect, useState} from "react";
import {Select, SelectOption} from "../common/MuiSelect";
import {isSuccess} from "../../client/Result";
import {GridRowId} from "@mui/x-data-grid";
import {SingleTable} from "../common/Tables";
import {ObjectWithId} from "../common/CrudTable";
import {MeterApi, MeterClient} from "../../client/MeterClient";

export interface DFSPageProps {
    dfsClient: DfsClient
    meterClient: MeterClient
    disableVirtualizationForTesting?: boolean
}

export const DFSBucketPage = ({dfsClient, meterClient, disableVirtualizationForTesting}: DFSPageProps) => {
    const [searchParams] = useSearchParams();
    const bucketId = searchParams.get('bucketId');

    const [rows, setRows] = useState<RowShape[] | undefined>();
    const [selectedBucketId, setSelectedBucketId] = useState<string | null>(bucketId);
    const [selectionModel, setSelectionModel] = useState<GridRowId[]>([]);

    const shaping = (mapping: BucketMapping[], meters: MeterApi[]): RowShape[] => {
        return mapping.flatMap(map => map.mpans.map((mpan, index) => {
            const meter = meters.find(m => m.mpan === mpan);
            return ({
                bucketId: map.id,
                building: meter?.hasSiteAttached ?? false ? meter?.description : undefined,
                mpan,
                queryableDataId: map.id + '-' + index
            });
        }))
    }

    useEffect(() => {
        const fetchData = async () => {
            const buckets = await dfsClient.retrieveDFSBuckets();
            if (isSuccess(buckets)) {
                const meters = await meterClient.retrieveMeters();
                setRows(shaping(buckets.value, meters))
            }
        };

        fetchData()
    }, [dfsClient, meterClient]);

    // Handle bucket selection change from the dropdown
    const handleBucketChange = (selectedOption: SelectOption | undefined) => {
        if (selectedOption) {
            setSelectedBucketId(selectedOption.id);
        }
    };

    interface RowShape extends ObjectWithId {
        bucketId: string,
        building? : string,
        mpan: string,
        queryableDataId: string
    }

    const renderTable = () => {
        if (rows) {
            const filteredRows = selectedBucketId
                ? rows.filter(bucket => bucket.bucketId === selectedBucketId)
                : rows;


            return (
                <SingleTable
                    disableVirtualization={disableVirtualizationForTesting}
                    data={filteredRows}
                    setSelectedData={() => {
                        null;
                    }}
                    selectionModel={selectionModel}
                    setSelectionModel={setSelectionModel}
                    dataTestId="bucket-table"
                    pageSize={50}
                    checkboxSelection={false}
                />
            )
        } else {
            return <div>Sorry, no data available.</div>
        }
    };

    // Generate the options for the Select dropdown
    const bucketOptions = rows
        ? [... new Set(rows.map(bucket => bucket.bucketId))].map(bucketId => ({label: bucketId, id: bucketId}))
        : [];

    return (
        <div>
            <div>
                <Select
                    initialValue={bucketOptions.find(option => option.id === selectedBucketId)}
                    options={bucketOptions}
                    label="Select a Bucket"
                    onChange={handleBucketChange}
                />
            </div>
            {renderTable()}
        </div>
    );
};
